import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { CalendarIcon, Plus, Search, Trash2, Edit, Download, MessageSquare, TrendingUp, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { EnhancedTable } from './EnhancedTable';
import { BulkActions } from './BulkActions';
import { SplitPane } from './SplitPane';

interface DailyMessage {
  id: string;
  title: string;
  body: string;
  type: string;
  start_date: string;
  end_date: string;
  target_crossing: string;
  link?: string;
  priority: number;
  created_at: string;
  updated_at: string;
}

interface MessageFormData {
  title: string;
  body: string;
  type: string;
  start_date: Date | undefined;
  end_date: Date | undefined;
  target_crossing: string;
  link: string;
  priority: number;
}

const MessagesManagement = () => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  
  const [messages, setMessages] = useState<DailyMessage[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<DailyMessage | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [crossingFilter, setCrossingFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState<DailyMessage | null>(null);
  const [metrics, setMetrics] = useState({
    totalMessages: 0,
    activeMessages: 0,
    totalViews: 0,
    totalClicks: 0
  });
  
  const [formData, setFormData] = useState<MessageFormData>({
    title: '',
    body: '',
    type: 'info',
    start_date: undefined,
    end_date: undefined,
    target_crossing: 'ALL',
    link: '',
    priority: 1
  });

  const messageTypes = [
    { value: 'info', label: isSpanish ? 'Información' : 'Info' },
    { value: 'alert', label: isSpanish ? 'Alerta' : 'Alert' },
    { value: 'offer', label: isSpanish ? 'Oferta' : 'Offer' },
    { value: 'holiday', label: isSpanish ? 'Día Festivo' : 'Holiday' }
  ];

  const crossings = [
    { value: 'ALL', label: isSpanish ? 'Todas las Garitas' : 'All Crossings' },
    { value: 'San Ysidro', label: 'San Ysidro' },
    { value: 'Otay Mesa', label: 'Otay Mesa' },
    { value: 'Tecate', label: 'Tecate' },
    { value: 'Calexico', label: 'Calexico' }
  ];

  useEffect(() => {
    fetchMessages();
    fetchMetrics();
  }, []);

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('daily_messages')
        .select('*')
        .order('start_date', { ascending: false });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error(isSpanish ? 'Error al cargar mensajes' : 'Error loading messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      const { count: totalMessages } = await supabase
        .from('daily_messages')
        .select('*', { count: 'exact', head: true });

      const today = new Date().toISOString().split('T')[0];
      const { count: activeMessages } = await supabase
        .from('daily_messages')
        .select('*', { count: 'exact', head: true })
        .lte('start_date', today)
        .gte('end_date', today);

      const { data: engagementData } = await supabase
        .from('message_engagement')
        .select('action');

      const totalViews = engagementData?.filter(e => e.action === 'view').length || 0;
      const totalClicks = engagementData?.filter(e => e.action === 'click').length || 0;

      setMetrics({
        totalMessages: totalMessages || 0,
        activeMessages: activeMessages || 0,
        totalViews,
        totalClicks
      });
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  };

  const columns = [
    {
      id: 'title',
      label: isSpanish ? 'Título' : 'Title',
      accessor: (msg: DailyMessage) => msg.title,
      sortable: true
    },
    {
      id: 'type',
      label: isSpanish ? 'Tipo' : 'Type',
      accessor: (msg: DailyMessage) => {
        const colors: Record<string, string> = {
          alert: 'destructive',
          offer: 'default',
          holiday: 'secondary',
          info: 'outline'
        };
        return <Badge variant={colors[msg.type] as any}>{msg.type}</Badge>;
      }
    },
    {
      id: 'crossing',
      label: isSpanish ? 'Garita' : 'Crossing',
      accessor: (msg: DailyMessage) => (
        <Badge variant="outline">{msg.target_crossing}</Badge>
      )
    },
    {
      id: 'dates',
      label: isSpanish ? 'Fechas' : 'Dates',
      accessor: (msg: DailyMessage) => (
        <div className="text-sm">
          <div>{format(new Date(msg.start_date), 'MMM d')}</div>
          <div className="text-muted-foreground">{format(new Date(msg.end_date), 'MMM d')}</div>
        </div>
      )
    },
    {
      id: 'status',
      label: isSpanish ? 'Estado' : 'Status',
      accessor: (msg: DailyMessage) => {
        const today = new Date().toISOString().split('T')[0];
        const isActive = msg.start_date <= today && msg.end_date >= today;
        return (
          <Badge variant={isActive ? "default" : "secondary"}>
            {isActive ? (isSpanish ? 'Activo' : 'Active') : (isSpanish ? 'Inactivo' : 'Inactive')}
          </Badge>
        );
      },
      sortable: true
    },
    {
      id: 'priority',
      label: isSpanish ? 'Prioridad' : 'Priority',
      accessor: (msg: DailyMessage) => (
        <Badge variant="outline">{msg.priority}</Badge>
      ),
      sortable: true
    }
  ];

  const filteredMessages = messages.filter(msg => {
    const matchesSearch = msg.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         msg.body.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || msg.type === typeFilter;
    const matchesCrossing = crossingFilter === 'all' || msg.target_crossing === crossingFilter;
    return matchesSearch && matchesType && matchesCrossing;
  });

  const bulkActions = [
    {
      id: 'delete',
      label: isSpanish ? 'Eliminar' : 'Delete',
      icon: Trash2,
      variant: 'destructive' as const,
      action: async () => {
        if (confirm(isSpanish ? '¿Eliminar mensajes seleccionados?' : 'Delete selected messages?')) {
          try {
            await supabase
              .from('daily_messages')
              .delete()
              .in('id', selectedIds);
            toast.success(isSpanish ? 'Mensajes eliminados' : 'Messages deleted');
            fetchMessages();
            setSelectedIds([]);
            setSelectedMessage(null);
          } catch (error) {
            toast.error('Error');
          }
        }
      }
    },
    {
      id: 'export',
      label: isSpanish ? 'Exportar' : 'Export',
      icon: Download,
      action: () => {
        const selectedData = messages.filter(msg => selectedIds.includes(msg.id));
        const csv = [
          ['Title', 'Body', 'Type', 'Crossing', 'Start Date', 'End Date', 'Priority'].join(','),
          ...selectedData.map(msg => [
            msg.title,
            msg.body,
            msg.type,
            msg.target_crossing,
            msg.start_date,
            msg.end_date,
            msg.priority
          ].join(','))
        ].join('\n');
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `messages_export_${Date.now()}.csv`;
        a.click();
        setSelectedIds([]);
      }
    }
  ];

  const resetForm = () => {
    setFormData({
      title: '',
      body: '',
      type: 'info',
      start_date: undefined,
      end_date: undefined,
      target_crossing: 'ALL',
      link: '',
      priority: 1
    });
    setEditingMessage(null);
  };

  const handleCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleEdit = (message: DailyMessage) => {
    setFormData({
      title: message.title,
      body: message.body,
      type: message.type,
      start_date: new Date(message.start_date),
      end_date: new Date(message.end_date),
      target_crossing: message.target_crossing,
      link: message.link || '',
      priority: message.priority
    });
    setEditingMessage(message);
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.body || !formData.start_date || !formData.end_date) {
      toast.error(isSpanish ? 'Por favor completa todos los campos requeridos' : 'Please fill in all required fields');
      return;
    }

    try {
      const messageData = {
        title: formData.title,
        body: formData.body,
        type: formData.type,
        start_date: formData.start_date.toISOString().split('T')[0],
        end_date: formData.end_date.toISOString().split('T')[0],
        target_crossing: formData.target_crossing,
        link: formData.link || null,
        priority: formData.priority
      };

      if (editingMessage) {
        const { error } = await supabase
          .from('daily_messages')
          .update(messageData)
          .eq('id', editingMessage.id);

        if (error) throw error;
        toast.success(isSpanish ? 'Mensaje actualizado' : 'Message updated');
      } else {
        const { error } = await supabase
          .from('daily_messages')
          .insert([messageData]);

        if (error) throw error;
        toast.success(isSpanish ? 'Mensaje creado' : 'Message created');
      }

      setDialogOpen(false);
      resetForm();
      fetchMessages();
    } catch (error) {
      console.error('Error saving message:', error);
      toast.error(isSpanish ? 'Error al guardar mensaje' : 'Error saving message');
    }
  };

  return (
    <div className="space-y-6">
      {/* Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {isSpanish ? 'Total de Mensajes' : 'Total Messages'}
            </CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalMessages}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {isSpanish ? 'Mensajes Activos' : 'Active Messages'}
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.activeMessages}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {isSpanish ? 'Vistas Totales' : 'Total Views'}
            </CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalViews.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {isSpanish ? 'Clics Totales' : 'Total Clicks'}
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalClicks.toLocaleString()}</div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={isSpanish ? "Buscar mensajes..." : "Search messages..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={isSpanish ? "Tipo" : "Type"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{isSpanish ? 'Todos' : 'All'}</SelectItem>
              {messageTypes.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={crossingFilter} onValueChange={setCrossingFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={isSpanish ? "Garita" : "Crossing"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{isSpanish ? 'Todas' : 'All'}</SelectItem>
              {crossings.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          {isSpanish ? 'Nuevo Mensaje' : 'New Message'}
        </Button>
      </div>

      {/* Bulk Actions */}
      <BulkActions
        selectedCount={selectedIds.length}
        actions={bulkActions}
        onClearSelection={() => setSelectedIds([])}
        isSpanish={isSpanish}
      />

      {/* Table with Split Pane */}
      <SplitPane
        left={
          <EnhancedTable
            data={filteredMessages}
            columns={columns}
            onRowClick={(msg) => setSelectedMessage(msg as DailyMessage)}
            selectable
            rowKey="id"
          />
        }
        right={
          selectedMessage ? (
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{isSpanish ? 'Detalles del Mensaje' : 'Message Details'}</h3>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(selectedMessage)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedMessage(null)}>×</Button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-1">{isSpanish ? 'Título' : 'Title'}</h4>
                  <p className="text-sm text-muted-foreground">{selectedMessage.title}</p>
                </div>

                <div>
                  <h4 className="font-medium mb-1">{isSpanish ? 'Mensaje' : 'Message'}</h4>
                  <p className="text-sm text-muted-foreground">{selectedMessage.body}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-1">{isSpanish ? 'Tipo' : 'Type'}</h4>
                    <Badge>{selectedMessage.type}</Badge>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">{isSpanish ? 'Prioridad' : 'Priority'}</h4>
                    <Badge variant="outline">{selectedMessage.priority}</Badge>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-1">{isSpanish ? 'Garita Objetivo' : 'Target Crossing'}</h4>
                  <Badge variant="outline">{selectedMessage.target_crossing}</Badge>
                </div>

                <div>
                  <h4 className="font-medium mb-1">{isSpanish ? 'Período' : 'Period'}</h4>
                  <div className="text-sm text-muted-foreground">
                    {format(new Date(selectedMessage.start_date), 'PPP')} - {format(new Date(selectedMessage.end_date), 'PPP')}
                  </div>
                </div>

                {selectedMessage.link && (
                  <div>
                    <h4 className="font-medium mb-1">{isSpanish ? 'Enlace' : 'Link'}</h4>
                    <a href={selectedMessage.link} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                      {selectedMessage.link}
                    </a>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              {isSpanish ? 'Selecciona un mensaje para ver detalles' : 'Select a message to view details'}
            </div>
          )
        }
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingMessage
                ? (isSpanish ? 'Editar Mensaje' : 'Edit Message')
                : (isSpanish ? 'Crear Nuevo Mensaje' : 'Create New Message')
              }
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">{isSpanish ? 'Título' : 'Title'} *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="body">{isSpanish ? 'Mensaje' : 'Message'} *</Label>
              <Textarea
                id="body"
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                rows={3}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{isSpanish ? 'Tipo' : 'Type'}</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {messageTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>{isSpanish ? 'Garita Objetivo' : 'Target Crossing'}</Label>
                <Select value={formData.target_crossing} onValueChange={(value) => setFormData({ ...formData, target_crossing: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {crossings.map((crossing) => (
                      <SelectItem key={crossing.value} value={crossing.value}>
                        {crossing.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{isSpanish ? 'Fecha de Inicio' : 'Start Date'} *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.start_date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.start_date ? format(formData.start_date, "PPP") : (isSpanish ? "Seleccionar fecha" : "Pick a date")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.start_date}
                      onSelect={(date) => setFormData({ ...formData, start_date: date })}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div>
                <Label>{isSpanish ? 'Fecha de Fin' : 'End Date'} *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.end_date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.end_date ? format(formData.end_date, "PPP") : (isSpanish ? "Seleccionar fecha" : "Pick a date")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.end_date}
                      onSelect={(date) => setFormData({ ...formData, end_date: date })}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="submit" className="flex-1">
                {editingMessage ? (isSpanish ? 'Actualizar' : 'Update') : (isSpanish ? 'Crear' : 'Create')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {isSpanish ? 'Cancelar' : 'Cancel'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagesManagement;
